import {animate, state, style, transition, trigger} from '@angular/animations';
import {AsyncPipe, CommonModule} from '@angular/common';
import {Component, computed, DestroyRef, effect, ElementRef, inject, OnInit, signal, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {MAT_MOMENT_DATE_FORMATS, provideMomentDateAdapter} from '@angular/material-moment-adapter';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectChange, MatSelectModule} from '@angular/material/select';
import {Router, RouterOutlet} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {AngularSvgIconModule} from 'angular-svg-icon';
import {format} from 'date-fns/format';
import {ClinicalRecordType} from 'src/domain/enum/clinical-record-type.enum';
import {Gender} from 'src/domain/enum/gender.enum';
import {TimelineService} from 'src/infrastructure/timeline/timeline.service';
import {ContainerComponent} from 'src/shared/container/container.component';
import {CustomButtonComponent} from 'src/shared/custom-button/custom-button.component';
import {FilterService} from 'src/shared/search-input/filter.service';
import {SearchInputComponent} from 'src/shared/search-input/search-input.component';
import {getKeyFromValue} from 'src/shared/utils/utils';
import {HabitComponent} from './habit/habit.component';
import {TimelineComponent} from './timeline/timeline.component';

@Component({
    selector: 'app-patient-timeline-information',
    templateUrl: './patient-timeline-information.component.html',
    styleUrls: ['./patient-timeline-information.component.scss'],
    standalone: true,
    providers: [
        provideMomentDateAdapter(),
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS }
    ],
    imports: [
        RouterOutlet,
        TranslateModule,
        ContainerComponent,
        CustomButtonComponent,
        AngularSvgIconModule,
        CommonModule,
        SearchInputComponent,
        HabitComponent,
        TimelineComponent,
        MatFormFieldModule,
        MatSelectModule,
        MatDatepickerModule,
        FormsModule,
        AsyncPipe
    ],
    animations: [
        trigger('filtersHeightAnimation', [
            state('true', style({ height: '*', paddingTop: '16px' })),
            state('false', style({ height: '0px', paddingTop: '0px' })),
            transition('true <=> false', animate('300ms'))
        ]),
        trigger('columnHeightAnimation', [
            state('true', style({ height: 'calc(100vh - 292px)'})),
            state('false', style({ height: 'calc(100vh - 220px)'})),
            transition('true <=> false', animate('300ms'))
        ])
    ]
})
export class PatientTimelineInformationComponent implements OnInit
{
    @ViewChild('timelineCard', {static: true}) public timelineCardElement: ElementRef;

    @ViewChild('pageHeader', {static: true}) public pageHeaderElement: ElementRef;

    @ViewChild('moreFilters', {static: true}) public moreFiltersElement: ElementRef;

    private readonly timelineService = inject(TimelineService);

    private readonly filterService = inject(FilterService);

    private readonly translate = inject(TranslateService);

    private readonly router = inject(Router);

    private readonly destroyRef = inject(DestroyRef);

    private readonly qrCodeToken = computed(() => this.timelineService.qrCodeToken());

    public readonly showFilters = signal<boolean>(false);

    public readonly isFetchingDetails = computed(() => this.timelineService.loading());

    public readonly patientDetails = computed(() => this.timelineService.patient());

    public readonly timelineRecords = computed(() => this.timelineService.clinicalRegistries());

    public readonly clinicalRecordType = Object.values(ClinicalRecordType).filter((option: ClinicalRecordType) => option !== ClinicalRecordType.ALL);

    public readonly filterParams = signal<{
        filter?: string;
        startDateFilter?: Date;
        endDateFilter?: Date;
        type?: ClinicalRecordType;
    }>({
        filter: undefined,
        startDateFilter: undefined,
        endDateFilter: undefined,
        type: undefined
    });

    public constructor()
    {
        effect(() =>
        {
            if (!this.timelineService.qrCodeToken())
            {
                this.router.navigateByUrl('public-timeline-sharing');
            }

            if (this.patientDetails() && this.timelineRecords())
            {
                this.timelineService.checkIfSharingStillValid();
            }
        });
    }

    public ngOnInit(): void
    {
        if (!!this.qrCodeToken())
        {
            this.getDetails();

            this.filterService.onFilterChanged()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((value: string) =>
                {
                    this.filterParams.set({
                        ...this.filterParams(),
                        filter: value
                    });

                    this.getDetails();
                });
        }
        else
        {
            this.router.navigateByUrl('public-timeline-sharing');
        }
    }

    public getDetails(): void
    {
        const {filter, startDateFilter, endDateFilter, type} = this.filterParams();

        this.timelineService.getPatientTimelineInformation(
            filter,
            !!startDateFilter ? format(new Date(startDateFilter), 'yyyy-MM-dd') : undefined,
            !!endDateFilter ? format(new Date(endDateFilter), 'yyyy-MM-dd') : undefined,
            type
        );
    }

    public closeTimelineSharing(): void
    {
        this.timelineService.closeTimelineSharing();
    }

    public closeFilters(): void
    {
        this.showFilters.set(!this.showFilters());

        this.filterParams.set({
            ...this.filterParams(),
            startDateFilter: undefined,
            endDateFilter: undefined,
            type: undefined
        });

        this.getDetails();
    }

    public filtersButtonLabel(): void
    {
        return this.translate.instant(`PATIENT_TIMELINE_INFORMATION.${this.showFilters() ? 'CLOSE_FILTERS' : 'FILTERS'}`);
    }

    public getGender(): string
    {
        return this.translate.instant(`GENERAL.GENDER.${getKeyFromValue(Gender, this.patientDetails().gender)}`);
    }

    public getTypeLabel(type: ClinicalRecordType): string
    {
        return this.translate.instant(`GENERAL.CLINICAL_REGISTRY_TYPE.${getKeyFromValue(ClinicalRecordType, type)}`);
    }

    public typeChanged(event: MatSelectChange): void
    {
        this.filterParams.set({
            ...this.filterParams(),
            type: event.value
        });

        this.getDetails();
    }
}
